// Persistence for the session data-encryption key.
//
// iOS stores the DEK in the Keychain. A browser has no equivalent, so we keep it in IndexedDB as a
// non-extractable CryptoKey: usable for encrypt/decrypt, but its raw bytes cannot be read back out
// by any script on the page. The key is scoped to the signed-in user id, so switching accounts on a
// shared computer can never decrypt the wrong person's journal.

const DB_NAME = 'oreia-vault';
const DB_VERSION = 1;
const STORE = 'keys';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const req = run(transaction.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        transaction.oncomplete = () => db.close();
      }),
  );
}

const keyFor = (userId: string) => `dek:${userId}`;

export async function loadStoredDEK(userId: string): Promise<CryptoKey | null> {
  try {
    const stored = await tx<CryptoKey | undefined>('readonly', (s) => s.get(keyFor(userId)));
    return stored ?? null;
  } catch {
    return null;
  }
}

/** `dek` must already be non-extractable — see crypto/vault.ts toNonExtractable. */
export async function storeDEK(userId: string, dek: CryptoKey): Promise<void> {
  try {
    await tx('readwrite', (s) => s.put(dek, keyFor(userId)));
  } catch {
    // A blocked or full IndexedDB should not break signing in; the user just has to unlock again
    // on the next page load.
  }
}

export async function clearStoredDEK(userId: string): Promise<void> {
  try {
    await tx('readwrite', (s) => s.delete(keyFor(userId)));
  } catch {
    // Nothing to do — a failed delete on sign-out is not worth surfacing.
  }
}

/** Wipe every stored key, e.g. on account deletion or an explicit "forget this device". */
export async function clearAllStoredDEKs(): Promise<void> {
  try {
    await tx('readwrite', (s) => s.clear());
  } catch {
    // Ignored for the same reason as above.
  }
}
